import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputProps,
  Spacer,
} from "@chakra-ui/react";
import moment from "moment";
import { ChangeEvent, ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { RanaExtraInfo } from "../../components/rana-extra-info";
import { useFormFieldError } from "../hooks/use-rana-form-field-error";
import { useRanaFormIsValid } from "../hooks/use-rana-form-is-valid";
import { RanaFormContext } from "../rana-form";

interface RanaFormInputProps extends InputProps {
  field: string;
  label?: string;
  explanation?: string;
  extra?: string;
  maxChar?: number;
  submitOnEnter?: boolean;
  extraInfo?: ReactNode;
  helpText?: (value: string) => ReactNode;
  isLowercase?: boolean;
}

export function RanaFormInput({
  field,
  label,
  explanation,
  extra,
  maxChar,
  submitOnEnter,
  isRequired,
  extraInfo,
  helpText,
  isLowercase,
  onChange,
  ...rest
}: RanaFormInputProps) {
  const { setPartialValues, values, addRequired, onSubmit } = useContext(RanaFormContext);

  const [active, setActive] = useState(false);

  const error = useFormFieldError(field);
  const stringValue = (values[field] || "") + "";
  const charRemaining = maxChar ? maxChar - stringValue.length : undefined;

  // Derive display value
  const value = (() => {
    // Doing new Date() with an emptry string gives "Invalid Date", so we check for that first
    if (!stringValue) return stringValue;

    if (rest.type === "datetime-local") {
      return moment(new Date(stringValue)).format("YYYY-MM-DDTHH:mm");
    }
    if (rest.type === "date") {
      return moment(new Date(stringValue)).format("YYYY-MM-DD");
    }
    return stringValue;
  })();

  useEffect(() => {
    if (isRequired) addRequired(field);
  }, [addRequired, field, isRequired]);

  const updateValues = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const isDate = rest.type === "date" || rest.type === "datetime-local";
      const isInvalidDate = isDate && new Date(raw).toString() === "Invalid Date";

      const processed = !isDate && isLowercase ? raw.toLowerCase() : raw;

      if (isInvalidDate) {
        setPartialValues({ [field]: null });
      } else if (isDate) {
        setPartialValues({ [field]: new Date(processed) });
      } else {
        setPartialValues({ [field]: processed });
      }
    },
    [field, rest.type, setPartialValues, isLowercase]
  );

  const ranaFormIsValid = useRanaFormIsValid();

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired}>
      <Flex direction="column" position="relative">
        {(label || charRemaining || extra) && (
          <Flex>
            <FormLabel>{label}</FormLabel> <Spacer />
            <FormLabel requiredIndicator={<Box />} mt="auto" fontSize="xs">
              {charRemaining !== undefined ? charRemaining + " left" : extra}
            </FormLabel>
          </Flex>
        )}

        <Flex gap={2} alignItems="flex-start">
          <Input
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            bg="brand.grey.100"
            isInvalid={!!error}
            value={value}
            placeholder={active ? "" : explanation}
            onChange={(e) => {
              updateValues(e);
              if (onChange) onChange(e);
            }}
            onKeyDown={(e) => submitOnEnter && e.key === "Enter" && ranaFormIsValid && onSubmit()}
            zIndex="base"
            mb="6"
            {...rest}
          />
          <RanaExtraInfo type="popover">{extraInfo}</RanaExtraInfo>
        </Flex>

        {!error && helpText && helpText(value + "") ? (
          <FormHelperText>{helpText(value + "")}</FormHelperText>
        ) : null}

        <FormErrorMessage>{error}</FormErrorMessage>
      </Flex>
    </FormControl>
  );
}
